<template>
  <panel-layout
    id="form-panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template v-if="customerForm" #actions-right>
      <div class="flex items-center space-x-1">
        <o-tooltip :text="$t('general.reservation')" class="h-8">
          <o-panel-link
            v-if="customerForm.reservationCustomer"
            tag="o-button"
            flat
            :path="`/appointment/attendee/${customerForm.reservationCustomer.idReservationCustomer}`"
            icon="reservation"
          />

          <o-panel-link
            v-if="customerForm.reservation"
            tag="o-button"
            flat
            :path="`/appointment/${customerForm.reservation.idReservation}?date=${customerForm.reservation.date}`"
            icon="reservation"
          />
        </o-tooltip>
      </div>
    </template>

    <template #heading>
      <div v-if="customerForm" class="flex items-center space-x-2">
        <div>
          <h2 class="text-lg md:text-2xl tracking-tighter font-bold text-gray-800/85 leading-none dark:text-gray-100">
            {{ customerForm.form?.title }}
          </h2>
          <div class="mt-2 mb-4 flex flex-wrap md:flex-no-wrap items-center text-gray-700 text-sm dark:text-gray-200">
            <div class="flex items-center mr-2 text-sm">
              <o-icon icon="customer" :size="14" class="mr-2" />
              <p class="leading-none">
                {{ customerForm.customer.displayName }}
              </p>
            </div>
            <div class="flex items-center text-sm">
              <o-icon icon="calendar" :size="13" class="block mr-2 pb-2px" />
              <p class="leading-none">
                {{ customerForm.createdLabel }}
              </p>
            </div>
          </div>
          <form-status :customerForm="customerForm" />
        </div>
      </div>
    </template>

    <div class="h-full">
      <o-scroll ref="scroll">
        <div v-if="customerForm" class="h-full p-5">
          <o-alert v-if="errors" :title="$t('pages.customer.form.id.incomplete')" variant="danger" class="mb-6" show>
            <p>{{ $t('general.fixError') }}</p>
            <ul class="list-disc">
              <li v-for="(error, key) in errors" :key="key" class="ml-4">
                {{ error[0] }}
              </li>
            </ul>
          </o-alert>
          <div class="pb-12">
            <form-controller :customerForm="customerForm" class="block" />
            <div v-if="showSendEmail" class="mb-8 mt-4 w-full border rounded p-4">
              <o-checkbox v-model="sendEmail" class="-mb-1" :label="$t('pages.customer.giftCard.id.emailCopy')" />
            </div>
          </div>
        </div>
      </o-scroll>
    </div>

    <template #footer>
      <div v-if="customerForm">
        <div class="flex justify-between align-items">
          <o-button outline flat class="mr-2" @click="cancel()">
            {{ $t('general.cancel') }}
          </o-button>
          <div class="flex items-center space-x-1">
            <o-button variant="primary" outline icon="save" class="mr-2" @click="save()">
              {{ $t('general.save') }}
            </o-button>
            <o-button variant="primary" icon="save" class="mr-2" @click="save(true)">
              {{ $t('general.saveAndClose') }}
            </o-button>
          </div>
        </div>
      </div>
    </template>

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        { text: $t('general.closeWithoutSaving'), type: 'confirm', variant: undefined, flat: true, outline: true, callback: () => cancel(true) },
        { text: $t('general.saveAndClose'), type: 'confirm', response: 'save', callback: () => save(true) }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">
// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'
// @ts-ignore
import FormController from '@/components/customerForm/edit/FormController.vue'
// @ts-ignore
import FormStatus from '@/components/customerForm/elements/Status.vue'

// @ts-ignore
import {
  OAlert,
  OCustomFields,
  ODescriptionDetails,
  OFieldGroup,
} from '@/components/elements'
// @ts-ignore
import OCheckbox from '@/components/fields/Checkbox.vue'
import { usePanel } from '~/composables/usePanel'

const props = withDefaults(defineProps<{
  id?: string
  defaults?: Record<string, any> | null
}>(), {
  defaults: null,
})

const { $api, $bus, $notification, $i18n } = useNuxtApp()

const panel = usePanel()

const scroll = ref<any>()
const confirmDialog = ref<any>()

const customerForm = ref<any>(null)
const errors = ref<any>(false)
const loading = ref(true)
const sendEmail = ref(false)

const { unsavedChanges, snapshot } = useUnsavedChanges(customerForm)

const showSendEmail = computed(() => {
  return customerForm.value?.form?.allowCustomerSendCopy
})

const save = async (close = false) => {
  loading.value = true
  errors.value = false

  try {
    customerForm.value.sendEmail = sendEmail.value
    const result = (await $api.customerForm.save(customerForm.value)).data

    if (result) {
      customerForm.value = result
      snapshot()

      $notification.toast({
        title: $i18n.t('pages.customer.form.id.success'),
        text: $i18n.t('pages.customer.form.id.successInfo'),
        variant: 'success',
      })

      $bus.emit.updated('CustomerForm', customerForm.value)

      if (customerForm.value.reservation) {
        $bus.emit.updated('Reservation', customerForm.value.reservation)
      }

      if (close) {
        panel.close()
      }
    }
  } catch (e: any) {
    errors.value = e?.errors
    loading.value = false

    scroll.value.scroll({ y: 0 })
    confirmDialog.value.close()

    return false
  }

  loading.value = false
}

const cancel = (force = false) => {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

const init = async (id?: string) => {
  loading.value = true
  try {
    if (props.defaults) {
      customerForm.value = props.defaults
    } else if (id) {
      customerForm.value = (await $api.customerForm.get(id)).data
    }
    snapshot()
  } catch (e) {
    console.log(e)
  }
  loading.value = false
}

watch(() => props.id, (id) => init(id), { immediate: true })

</script>
